import { useState, useMemo } from 'react'
import { ChevronDown, ChevronRight, DollarSign, Zap, Clock, Activity } from 'lucide-react'

interface TelemetryNode {
 node: string
 model: string
 provider: string
 input_tokens: number
 output_tokens: number
 cache_read_tokens: number
 /**
  * Tokens a model spent thinking before answering. Billed as output, and on
  * gpt-oss they can exceed the answer itself, so a row without them reads as
  * mysteriously expensive for its size.
  */
 reasoning_tokens?: number
 /** Attempts, including ones that failed and were billed anyway. */
 attempts?: number
 errors?: number
 latency_ms: number
 cost_usd: number
}

interface TelemetrySummary {
 nodes: TelemetryNode[]
 total_cost_usd: number
 total_input_tokens: number
 total_output_tokens: number
 total_cache_read_tokens: number
 total_tokens: number
 total_llm_calls: number
 total_latency_ms: number
 pipeline_had_revision: boolean
 providers: Array<{ provider: string; model_count: number; total_tokens: number; total_cost_usd: number }>
}

interface SessionCost {
 totalCost: number
 totalInputTokens: number
 totalOutputTokens: number
 totalTokens: number
 requestCount: number
 lastMessageCost?: number
 currentModel?: string
 lastTelemetry?: TelemetrySummary
}

interface CostTrackerProps {
 sessionCost: SessionCost
 className?: string
}

function formatCost(usd: number): string {
 if (usd === 0) return '$0'
 if (usd < 0.001) return `$${usd.toFixed(6)}`
 if (usd < 0.01) return `$${usd.toFixed(4)}`
 return `$${usd.toFixed(4)}`
}

function formatTokens(n: number): string {
 if (n === 0) return '0'
 if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
 return n.toString()
}

function formatLatency(ms: number): string {
 if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`
 return `${Math.round(ms)}ms`
}

export function CostTracker({ sessionCost, className = '' }: CostTrackerProps) {
 const [isExpanded, setIsExpanded] = useState(false)
 const [showLastTurn, setShowLastTurn] = useState(false)

 const avgLatency = useMemo(() => {
 if (!sessionCost.lastTelemetry || sessionCost.requestCount === 0) return 0
 return sessionCost.lastTelemetry.total_latency_ms
 }, [sessionCost])

 const modelBreakdown = useMemo(() => {
 const telem = sessionCost.lastTelemetry
 if (!telem?.nodes?.length) return []
 const map = new Map<string, { tokens: number; cost: number; calls: number }>()
 for (const n of telem.nodes) {
 const key = `${n.provider}/${n.model}`
 const prev = map.get(key) || { tokens: 0, cost: 0, calls: 0 }
 map.set(key, {
 tokens: prev.tokens + n.input_tokens + n.output_tokens,
 cost: prev.cost + n.cost_usd,
 calls: prev.calls + 1,
 })
 }
 return Array.from(map.entries()).map(([model, stats]) => ({ model, ...stats }))
 .sort((a, b) => b.cost - a.cost)
 }, [sessionCost.lastTelemetry])

 if (sessionCost.requestCount === 0) return null

 return (
 <div className={`border-t border-soft bg-surface-alt/50 ${className}`}>
 {/* Summary bar */}
 <button
 onClick={() => setIsExpanded(!isExpanded)}
 className="w-full flex items-center justify-between px-4 py-1.5 text-xs text-muted hover:bg-surface-alt/50 transition-colors"
 >
 <div className="flex items-center gap-3 font-mono">
 <span className="flex items-center gap-1">
 <DollarSign className="w-3 h-3" />
 {formatCost(sessionCost.totalCost)}
 </span>
 <span className="text-muted">|</span>
 <span className="flex items-center gap-1">
 <Zap className="w-3 h-3" />
 {formatTokens(sessionCost.totalTokens)} tok
 </span>
 <span className="text-muted">|</span>
 <span>{sessionCost.requestCount} turn{sessionCost.requestCount !== 1 ? 's' : ''}</span>
 {avgLatency > 0 && (
 <>
 <span className="text-muted">|</span>
 <span className="flex items-center gap-1">
 <Clock className="w-3 h-3" />
 {formatLatency(avgLatency)}
 </span>
 </>
 )}
 {sessionCost.lastTelemetry?.pipeline_had_revision && (
 <>
 <span className="text-muted">|</span>
 <span className="text-warn">revised</span>
 </>
 )}
 </div>
 <div className="flex items-center gap-1">
 <span className="text-muted text-app-micro">LLM costs</span>
 {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
 </div>
 </button>

 {/* Expanded detail */}
 {isExpanded && (
 <div className="px-4 pb-3 space-y-2">
 {/* Session aggregates */}
 <div className="grid grid-cols-4 gap-2 text-xs">
 <div className="bg-surface rounded p-2 border border-soft">
 <div className="text-muted text-app-micro uppercase tracking-wide">Total Cost</div>
 <div className="font-mono font-medium text-secondary">{formatCost(sessionCost.totalCost)}</div>
 </div>
 <div className="bg-surface rounded p-2 border border-soft">
 <div className="text-muted text-app-micro uppercase tracking-wide">Input Tok</div>
 <div className="font-mono font-medium text-secondary">{formatTokens(sessionCost.totalInputTokens)}</div>
 </div>
 <div className="bg-surface rounded p-2 border border-soft">
 <div className="text-muted text-app-micro uppercase tracking-wide">Output Tok</div>
 <div className="font-mono font-medium text-secondary">{formatTokens(sessionCost.totalOutputTokens)}</div>
 </div>
 <div className="bg-surface rounded p-2 border border-soft">
 <div className="text-muted text-app-micro uppercase tracking-wide">Last Turn</div>
 <div className="font-mono font-medium text-secondary">{formatCost(sessionCost.lastMessageCost || 0)}</div>
 </div>
 </div>

 {/* Model breakdown */}
 {modelBreakdown.length > 0 && (
 <div className="text-xs">
 <div className="text-muted text-app-micro uppercase tracking-wide mb-1">Models (last turn)</div>
 <div className="space-y-0.5">
 {modelBreakdown.map(({ model, tokens, cost, calls }) => (
 <div key={model} className="flex items-center justify-between font-mono text-secondary bg-surface rounded px-2 py-0.5 border border-soft">
 <span className="truncate max-w-[200px]">{model}</span>
 <div className="flex items-center gap-3 shrink-0">
 <span>{calls}x</span>
 <span>{formatTokens(tokens)}</span>
 <span className="text-ok">{formatCost(cost)}</span>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Last turn per-node detail */}
 {sessionCost.lastTelemetry?.nodes && sessionCost.lastTelemetry.nodes.length > 0 && (
 <div className="text-xs">
 <button
 onClick={() => setShowLastTurn(!showLastTurn)}
 className="flex items-center gap-1 text-muted text-app-micro uppercase tracking-wide mb-1 hover:text-secondary"
 >
 <Activity className="w-3 h-3" />
 Pipeline nodes ({sessionCost.lastTelemetry.nodes.length})
 {showLastTurn ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
 </button>
 {showLastTurn && (
 <div className="overflow-x-auto">
 <table className="w-full text-app-micro font-mono">
 <thead>
<tr className="text-muted text-left">
<th className="pr-2 py-0.5 font-normal">Node</th>
{/* Provider is its own column, not glued to the model. The same model id is
    sold by several vendors at several prices — "gpt-oss-120b" alone does not
    say who was paid, so it cannot be checked against a bill. */}
<th className="pr-2 py-0.5 font-normal">Provider</th>
<th className="pr-2 py-0.5 font-normal">Model</th>
<th className="pr-2 py-0.5 font-normal text-right">In</th>
<th className="pr-2 py-0.5 font-normal text-right">Out</th>
<th className="pr-2 py-0.5 font-normal text-right">Cache</th>
<th className="pr-2 py-0.5 font-normal text-right">Think</th>
<th className="pr-2 py-0.5 font-normal text-right">Try</th>
<th className="pr-2 py-0.5 font-normal text-right">Latency</th>
<th className="py-0.5 font-normal text-right">Cost</th>
</tr>
</thead>
<tbody>
{sessionCost.lastTelemetry.nodes.map((n, i) => (
<tr key={i} className="text-secondary border-t border-soft/50">
<td className="pr-2 py-0.5 text-muted">{n.node}</td>
<td className="pr-2 py-0.5 truncate max-w-[90px]">{n.provider}</td>
<td className="pr-2 py-0.5 truncate max-w-[120px]">{n.model}</td>
<td className="pr-2 py-0.5 text-right">{formatTokens(n.input_tokens)}</td>
<td className="pr-2 py-0.5 text-right">{formatTokens(n.output_tokens)}</td>
<td className="pr-2 py-0.5 text-right text-brand">{n.cache_read_tokens > 0 ? formatTokens(n.cache_read_tokens) : '-'}</td>
{/* Output tokens spent thinking. Already counted in Out; shown because it is
    the difference between an expensive model and a long prompt. */}
<td className="pr-2 py-0.5 text-right text-muted">{(n.reasoning_tokens ?? 0) > 0 ? formatTokens(n.reasoning_tokens!) : '-'}</td>
{/* Attempts. Anything above 1 was paid for twice, so it is worth seeing. */}
<td className={`pr-2 py-0.5 text-right ${(n.errors ?? 0) > 0 ? 'text-warn' : 'text-muted'}`}>{(n.attempts ?? 0) > 1 ? n.attempts : '-'}</td>
<td className="pr-2 py-0.5 text-right">{formatLatency(n.latency_ms)}</td>
<td className="py-0.5 text-right text-ok">{formatCost(n.cost_usd)}</td>
</tr>
))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 )}
 </div>
 )}
 </div>
 )
}
